import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import { MantineProvider } from "@mantine/core";
import { DatesProvider, type DatesProviderSettings } from "@mantine/dates";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { IconContext, type IconProps } from "@phosphor-icons/react";

import { DEFAULT_COLORSCHEME } from "@/configs/colorScheme";
import { NOTIFICATIONS_CONFIG } from "@/configs/notifications";
import { useWhitelabelBrand } from "@/hooks/useWhitelabelBrand";
import createTheme from "@/styles/theme";

const ICON_CONFIG: IconProps = {
  size: 16,
  weight: "regular",
  style: {
    minWidth: 12,
  },
};

const DATES_CONFIG: DatesProviderSettings = {
  consistentWeeks: true,
  firstDayOfWeek: 1, // monday
  weekendDays: [0, 6], // saturday, sunday
};

export const UiProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();
  const { whitelabelBrand } = useWhitelabelBrand();
  const theme = createTheme(whitelabelBrand.theme);
  return (
    <MantineProvider theme={theme} defaultColorScheme={DEFAULT_COLORSCHEME}>
      <IconContext value={ICON_CONFIG}>
        <DatesProvider settings={{ ...DATES_CONFIG, locale: i18n.resolvedLanguage }}>
          <Notifications {...NOTIFICATIONS_CONFIG} />
          <ModalsProvider>{children}</ModalsProvider>
        </DatesProvider>
      </IconContext>
    </MantineProvider>
  );
};

export default UiProvider;
